import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Flame, Star, Snowflake, Search, RefreshCw, Phone, MessageCircle,
    Mail, LogOut, Calendar, ArrowLeft, Loader2, ChevronDown,
} from "lucide-react";
import { api, waUrl } from "../lib/api";
import { toast } from "sonner";

const TIER_META = {
    HOT:  { label: "HOT",  color: "#DC2626", bg: "bg-red-50",    border: "border-red-200",    icon: Flame,     emoji: "🔥" },
    WARM: { label: "WARM", color: "#F5A623", bg: "bg-amber-50",  border: "border-amber-200",  icon: Star,      emoji: "🌟" },
    COLD: { label: "COLD", color: "#0284C7", bg: "bg-sky-50",    border: "border-sky-200",    icon: Snowflake, emoji: "❄️" },
};

const TIER_ORDER = { HOT: 0, WARM: 1, COLD: 2 };

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
    } catch (_e) {
        return iso;
    }
}

function cleanPhone(s) {
    const digits = (s || "").replace(/[^\d]/g, "");
    if (!digits) return "";
    return digits.startsWith("0") ? "62" + digits.slice(1) : digits;
}

function filterAndSortLeads(leads, tier, search) {
    const q = search.trim().toLowerCase();
    const tierFiltered = tier === "all" ? leads : leads.filter((it) => it.tier === tier);
    const searchFiltered = !q ? tierFiltered : tierFiltered.filter((it) =>
        (it.name || "").toLowerCase().includes(q) ||
        (it.email || "").toLowerCase().includes(q) ||
        (it.whatsapp || "").toLowerCase().includes(q) ||
        (it.source || "").toLowerCase().includes(q),
    );
    return [...searchFiltered].sort((a, b) => {
        const t = (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9);
        if (t !== 0) return t;
        return (b.created_at || "").localeCompare(a.created_at || "");
    });
}

async function doLogout() {
    try {
        await api.post("/auth/logout");
    } catch (err) {
        // Logout failure is non-blocking — server-side cookie will expire eventually.
        if (process.env.NODE_ENV !== "production") console.warn("[admin] logout failed", err);
    }
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [admin, setAdmin] = useState(null);
    const [authChecking, setAuthChecking] = useState(true);
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tier, setTier] = useState("all");
    const [search, setSearch] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/admin/me");
                setAdmin(r.data);
            } catch (e) {
                if (e?.response?.status === 403) {
                    toast.error("Akun ini bukan admin Gantari Edu.");
                    navigate("/dashboard");
                    return;
                }
                navigate("/login?next=/admin");
                return;
            } finally {
                setAuthChecking(false);
            }
            loadAll();
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [statsR, leadsR] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/leads", { params: { tier: "all", limit: 300 } }),
            ]);
            setStats(statsR.data);
            setLeads(leadsR.data.leads || []);
        } catch (_e) {
            toast.error("Gagal memuat data. Coba refresh.");
        } finally {
            setLoading(false);
        }
    };

    const filtered = filterAndSortLeads(leads, tier, search);

    if (authChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
                <Loader2 className="w-6 h-6 animate-spin text-[#0A1A2F]" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
            {/* Top bar */}
            <header className="bg-[#0A1A2F] text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate("/")} className="text-white/60 hover:text-white" data-testid="admin-back-home">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="text-[10px] tracking-[0.2em] text-[#F5A623] font-bold">GANTARI EDU · ADMIN</div>
                            <div className="text-lg font-bold">Lead Pipeline</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-2 text-sm">
                            {admin?.picture && <img src={admin.picture} alt="" className="w-7 h-7 rounded-full" />}
                            <span className="text-white/80">{admin?.email}</span>
                        </div>
                        <button
                            onClick={() => {
                                api.post("/auth/logout").catch(() => {}).finally(() => navigate("/"));
                            }}
                            className="text-sm text-white/70 hover:text-white flex items-center gap-1"
                            data-testid="admin-logout"
                        >
                            <LogOut className="w-4 h-4" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats cards */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
                    <StatCard label="Total Leads" value={stats?.total ?? "—"} accent="#0A1A2F" />
                    <StatCard label="🔥 HOT" value={stats?.by_tier?.HOT ?? 0} accent="#DC2626" subtitle="SLA: 1 jam" />
                    <StatCard label="🌟 WARM" value={stats?.by_tier?.WARM ?? 0} accent="#F5A623" subtitle="SLA: 4 jam" />
                    <StatCard label="❄️ COLD" value={stats?.by_tier?.COLD ?? 0} accent="#0284C7" subtitle="SLA: 24 jam" />
                </section>

                {/* Filters */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                    <div className="flex flex-wrap gap-2" data-testid="admin-tier-filter">
                        {["all", "HOT", "WARM", "COLD"].map((t) => (
                            <button
                                key={t}
                                onClick={() => setTier(t)}
                                data-testid={`tier-filter-${t}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                                    tier === t
                                        ? "bg-[#0A1A2F] text-white"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                            >
                                {t === "all" ? "Semua" : `${TIER_META[t].emoji} ${t}`}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 flex-1 md:max-w-md">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Cari nama, email, WA, atau sumber…"
                                data-testid="admin-search"
                                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:border-[#0A1A2F] outline-none"
                            />
                        </div>
                        <button onClick={loadAll} disabled={loading} className="p-2 text-slate-500 hover:text-[#0A1A2F]" data-testid="admin-refresh">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                </div>

                {/* Leads list */}
                <div className="space-y-2" data-testid="admin-leads-list">
                    <div className="text-xs text-slate-500 mb-2">
                        Menampilkan <strong>{filtered.length}</strong> dari {leads.length} lead · diurut HOT → WARM → COLD, lalu terbaru.
                    </div>
                    {filtered.length === 0 ? (
                        <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center text-sm text-slate-400">
                            Tidak ada lead untuk filter ini.
                        </div>
                    ) : (
                        filtered.map((lead) => (
                            <LeadRow
                                key={lead.lead_id}
                                lead={lead}
                                expanded={expandedId === lead.lead_id}
                                onToggle={() => setExpandedId(expandedId === lead.lead_id ? null : lead.lead_id)}
                            />
                        ))
                    )}
                </div>
            </main>
        </div>
    );
}

function StatCard({ label, value, accent, subtitle }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-2xl p-5"
        >
            <div className="text-xs font-bold tracking-widest uppercase text-slate-500">{label}</div>
            <div className="text-3xl font-extrabold mt-1" style={{ color: accent }}>{value}</div>
            {subtitle && <div className="text-[11px] text-slate-400 mt-1">{subtitle}</div>}
        </motion.div>
    );
}

function LeadRow({ lead, expanded, onToggle }) {
    const meta = TIER_META[lead.tier] || TIER_META.COLD;
    const Icon = meta.icon;
    const phone = cleanPhone(lead.whatsapp);
    const waMessage = `Halo Kak ${lead.name || ""}, saya dari Tim Konsultan Gantari Edu — terima kasih sudah submit ${lead.source} kami. Boleh ngobrol sebentar?`;

    return (
        <div className={`bg-white border ${meta.border} rounded-2xl overflow-hidden`} data-testid={`lead-row-${lead.lead_id}`}>
            <button
                onClick={onToggle}
                className="w-full px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 text-left hover:bg-slate-50"
            >
                <div className={`flex items-center gap-2 ${meta.bg} px-2.5 py-1 rounded-full self-start`} style={{ color: meta.color }}>
                    <Icon className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold tracking-wider">{meta.label} · {lead.score}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0A1A2F] truncate">{lead.name || "—"}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span>{lead.source}</span>
                        {lead.email && <span>· {lead.email}</span>}
                        {lead.whatsapp && <span>· {lead.whatsapp}</span>}
                        <span>· {formatDate(lead.created_at)}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-center">
                    {phone && (
                        <>
                            <a
                                href={waUrl(waMessage)}
                                target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                data-cta="cta_admin_whatsapp"
                                data-testid={`lead-wa-${lead.lead_id}`}
                                className="inline-flex items-center gap-1 bg-[#25D366] text-white text-xs font-bold rounded-full px-3 py-1.5 hover:bg-[#1FB454]"
                            >
                                <MessageCircle className="w-3.5 h-3.5" /> WA
                            </a>
                            <a
                                href={`tel:+${phone}`}
                                onClick={(e) => e.stopPropagation()}
                                data-cta="cta_admin_phone"
                                className="inline-flex items-center gap-1 bg-slate-100 text-[#0A1A2F] text-xs font-bold rounded-full px-3 py-1.5 hover:bg-slate-200"
                            >
                                <Phone className="w-3.5 h-3.5" /> Call
                            </a>
                        </>
                    )}
                    {lead.email && (
                        <a
                            href={`mailto:${lead.email}`}
                            onClick={(e) => e.stopPropagation()}
                            data-cta="cta_admin_email"
                            className="inline-flex items-center gap-1 bg-slate-100 text-[#0A1A2F] text-xs font-bold rounded-full px-3 py-1.5 hover:bg-slate-200"
                        >
                            <Mail className="w-3.5 h-3.5" />
                        </a>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </div>
            </button>
            {expanded && (
                <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50/50">
                    {lead.signals?.length > 0 && (
                        <div className="mt-3">
                            <div className="text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1.5">Signal Skor</div>
                            <ul className="text-xs text-[#0A1A2F] list-disc list-inside space-y-0.5">
                                {lead.signals.map((s, i) => <li key={`${s}-${i}`}>{s}</li>)}
                            </ul>
                        </div>
                    )}
                    {lead.payload && Object.keys(lead.payload).length > 0 && (
                        <div className="mt-3">
                            <div className="text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1.5">Submission Payload</div>
                            <div className="bg-white border border-slate-200 rounded-lg p-2 text-xs grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                                {Object.entries(lead.payload).map(([k, v]) => (
                                    <div key={k} className="truncate">
                                        <span className="text-slate-500">{k}:</span>{" "}
                                        <span className="font-semibold text-[#0A1A2F]">{
                                            typeof v === "object" ? JSON.stringify(v).slice(0, 80) : String(v).slice(0, 80)
                                        }</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                        <Calendar className="w-3.5 h-3.5" /> Lead ID: <code className="font-mono">{lead.lead_id}</code>
                    </div>
                </div>
            )}
        </div>
    );
}
